import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PublicHeader from './components/PublicHeader';
import PublicFooter from './components/PublicFooter';
import { denyAnalyticsConsent } from '../../components/GoogleAnalytics';

const GA_ID = (process.env.REACT_APP_GA_MEASUREMENT_ID || '').replace(/^G-/, '');

const essentialCookies = [
  {
    name: 'cookie_consent',
    purpose: "Remembers your choice here so we don't ask again.",
    lifetime: 'Until you clear it',
  },
  {
    name: 'uh_token / uh_refresh',
    purpose: 'Admin login session (only set if you log in as staff).',
    lifetime: 'Session (12 h / 30 d)',
  },
];

const analyticsCookies = [
  {
    name: '_ga',
    purpose: 'Google Analytics — distinguishes unique visitors.',
    lifetime: '2 years',
  },
  {
    name: GA_ID ? `_ga_${GA_ID}` : '_ga_<ID>',
    purpose: 'Google Analytics — session state for our GA4 property.',
    lifetime: '2 years',
  },
];

const Row = ({ c }) => (
  <tr className="border-b border-slate-200 last:border-0">
    <td className="py-3 px-4 align-top font-mono text-xs text-slate-900 whitespace-nowrap">{c.name}</td>
    <td className="py-3 px-4 align-top text-sm text-slate-700">{c.purpose}</td>
    <td className="py-3 px-4 align-top text-sm text-slate-500 whitespace-nowrap">{c.lifetime}</td>
  </tr>
);

const CookiePolicyPage = () => {
  const [consent, setConsent] = useState(null);
  const [withdrawn, setWithdrawn] = useState(false);

  const readConsent = () => {
    try {
      const raw = localStorage.getItem('cookie_consent');
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  };

  useEffect(() => {
    setConsent(readConsent());
  }, []);

  const handleOpenSettings = () => {
    window.dispatchEvent(new CustomEvent('open-cookie-settings'));
  };

  const handleWithdraw = () => {
    denyAnalyticsConsent();
    try { localStorage.removeItem('cookie_consent'); } catch (_) { /* noop */ }
    setConsent(null);
    setWithdrawn(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const hasGrantedAnalytics = consent && consent.analytics === true;

  const choiceLabel = !consent
    ? 'Not set — the banner will appear on your next visit.'
    : hasGrantedAnalytics
      ? 'Accept all (analytics enabled).'
      : 'Essential only (no analytics).';

  const lastUpdated = 'October 6, 2026';

  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2" data-testid="cookie-policy-title">Cookie Policy</h1>
        <p className="text-sm text-slate-500 mb-10">Last Updated: {lastUpdated}</p>

        {withdrawn && (
          <div
            className="mb-8 rounded-lg bg-green-50 border border-green-200 p-4 text-sm text-green-900"
            data-testid="consent-withdrawn-banner"
          >
            Analytics consent withdrawn. Any <code className="font-mono">_ga</code> / <code className="font-mono">_ga_{GA_ID || '<ID>'}</code> cookies have been cleared and Google Analytics is no longer loaded.
          </div>
        )}

        {/* ===== What Are Cookies ===== */}
        <section className="mb-10">
          <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-3">What Are Cookies</h2>
          <p className="text-base text-slate-700 leading-relaxed">
            Cookies are small text files stored on your device when you visit a website. They help websites function
            properly, remember your preferences, and provide analytics about how the site is used. Some sites
            (including this one) also use local storage, which works the same way from a privacy standpoint.
          </p>
        </section>

        {/* ===== Your Two Choices ===== */}
        <section className="mb-10">
          <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-3">Your Two Choices</h2>
          <p className="text-base text-slate-700 leading-relaxed mb-6">
            When you first visit <strong>sptdigitalmarketing.com</strong>, our cookie banner asks you to choose:
          </p>

          {/* Essential Only */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Essential Only</h3>
            <p className="text-sm text-slate-600 mb-4">
              We set only the cookies and local-storage keys needed to run the website. No analytics, no advertising,
              no tracking of any kind. The site does load Google Fonts (for typography) — these requests deliver
              font files only and do not set cookies or identify you.
            </p>
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full text-left">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Name</th>
                    <th className="py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Purpose</th>
                    <th className="py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Lifetime</th>
                  </tr>
                </thead>
                <tbody>
                  {essentialCookies.map((c) => <Row key={c.name} c={c} />)}
                </tbody>
              </table>
            </div>
          </div>

          {/* Accept All */}
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Accept All</h3>
            <p className="text-sm text-slate-600 mb-4">
              Everything above, plus <strong>Google Analytics 4</strong> so we can see which pages visitors read,
              how long they stay, and which marketing channels bring them to us. The data is aggregated and does
              not identify you personally.
            </p>
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full text-left">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Name</th>
                    <th className="py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Purpose</th>
                    <th className="py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Lifetime</th>
                  </tr>
                </thead>
                <tbody>
                  {analyticsCookies.map((c) => <Row key={c.name} c={c} />)}
                </tbody>
              </table>
            </div>
            <p className="text-sm text-slate-600 mt-3">
              Google Analytics data is processed by Google LLC. You can opt out of GA across all websites at{' '}
              <a
                href="https://tools.google.com/dlpage/gaoptout"
                target="_blank"
                rel="noreferrer"
                className="underline text-slate-900 hover:text-black"
              >
                tools.google.com/dlpage/gaoptout
              </a>.
            </p>
          </div>
        </section>

        {/* ===== What We Do Not Do ===== */}
        <section className="mb-10">
          <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-3">What We Do Not Do</h2>
          <p className="text-base text-slate-700 leading-relaxed">
            We do not use cookies to serve advertising, build advertising profiles, track you across other websites,
            or sell your data to third parties. The Google Analytics tracker loads only after you click{' '}
            <strong>"Accept all"</strong> — not before.
          </p>
        </section>

        {/* ===== Hosting Platform Disclosure ===== */}
        <section className="mb-10">
          <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-3">Hosting Platform Disclosure</h2>
          <p className="text-base text-slate-700 leading-relaxed">
            Our website is hosted on the Emergent platform. Emergent may collect basic usage and performance data
            about visits through its analytics provider (PostHog). We do not control this collection and do not
            receive or use this data. Questions about Emergent's data practices can be directed to{' '}
            <a href="mailto:support@emergent.sh" className="underline text-slate-900 hover:text-black">
              support@emergent.sh
            </a>.
          </p>
        </section>

        {/* ===== GPC / DNT ===== */}
        <section className="mb-10">
          <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-3">
            Global Privacy Control (GPC)
          </h2>
          <p className="text-base text-slate-700 leading-relaxed">
            If your browser sends a Global Privacy Control (GPC) signal, we treat it as a request to opt out of
            analytics automatically — the cookie banner will not appear and no analytics scripts will load.
            Browsers that support GPC include Firefox, Brave, and DuckDuckGo. You can still manually opt in on
            the banner or via this page if you change your mind.
          </p>
        </section>

        {/* ===== Changing / Withdrawing ===== */}
        <section className="mb-10">
          <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-3">Changing or Withdrawing Your Choice</h2>
          <p className="text-base text-slate-700 leading-relaxed mb-4">
            You can change your mind at any time. Withdrawing consent is as easy as giving it.
          </p>
          <p
            className="text-sm text-slate-700 mb-5"
            data-testid="current-choice-indicator"
          >
            <strong>Your current choice:</strong> {choiceLabel}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={handleOpenSettings}
              data-testid="change-cookie-settings-btn"
              className="h-11 rounded-md px-5 text-sm font-semibold text-white hover:opacity-90 transition-opacity uppercase tracking-wide"
              style={{ backgroundColor: '#1F3C4F' }}
            >
              Change Cookie Settings
            </button>
            {hasGrantedAnalytics && (
              <button
                type="button"
                onClick={handleWithdraw}
                data-testid="withdraw-analytics-consent-btn"
                className="h-11 rounded-md px-5 text-sm font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
              >
                Withdraw Analytics Consent
              </button>
            )}
          </div>
          <p className="text-sm text-slate-600 mt-5 leading-relaxed">
            You can also control or delete cookies through your browser settings. Most browsers let you refuse new
            cookies, delete existing cookies, or be notified when a new cookie is set. Disabling essential cookies
            may affect site functionality (for example, our banner will keep reappearing).
          </p>
        </section>

        {/* ===== Contact ===== */}
        <section className="mb-6">
          <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-3">Contact</h2>
          <p className="text-base text-slate-700">
            If you have questions about our use of cookies, contact us at{' '}
            <a href="mailto:info@sptdigitalmarketing.com" className="underline text-slate-900 hover:text-black">
              info@sptdigitalmarketing.com
            </a>.
          </p>
          <p className="text-sm text-slate-500 mt-6">
            See also our{' '}
            <Link to="/privacy-policy" className="underline text-slate-900 hover:text-black">Privacy Policy</Link>.
          </p>
        </section>
      </main>
      <PublicFooter />
    </div>
  );
};

export default CookiePolicyPage;
